import React from "react";
import Container from "../../components/Container/Container";
import { useAppContext } from "../../context/AppContext";
import { useAuth0 } from "@auth0/auth0-react";
import "./dashboard.scss";
import CertificateModal from "./components/CertificateModal";
import DashboardIntro from "./components/DashboardIntro";
import { getProductDefinition } from "../../config/products";
import { SectionRouteData } from "../../types/content";
import { useCertificateFlow } from "./hooks/useCertificateFlow";
import { useDashboardProgress } from "./hooks/useDashboardProgress";

interface DashboardProps {
  sections: SectionRouteData[];
}

const Dashboard: React.FC<DashboardProps> = ({ sections }) => {
  const { selectedProduct, isEnglish } = useAppContext();
  const certificateConfig = getProductDefinition(selectedProduct)?.certificate;
  const { user } = useAuth0();
  const { allSectionsCompleted, enabledSections, handleRestartClick, handleSectionClick } =
    useDashboardProgress({
      selectedProduct,
      sectionsCount: sections.length,
      user,
    });
  const {
    agreed,
    birthDate,
    emailError,
    emailSent,
    fieldCode,
    handleSendCertificateEmail,
    isPdfModalOpen,
    name,
    optionalEmail,
    pdfBlob,
    sendingEmail,
    setAgreed,
    setBirthDate,
    setFieldCode,
    setName,
    setPdfModalOpen,
    setSurname,
    surname,
    userData,
  } = useCertificateFlow({
    certificateConfig,
    selectedProduct,
    user,
  });

  return (
    <Container type="wide">
      <div className={"dashboard"}>
        {allSectionsCompleted && selectedProduct === "firstaid" && (
          <>
            <div className="dashboard__message-container">
              <p className="dashboard__message">
                Testas atliktas. Su jumis susisieks <strong>Firstaid.lt</strong> konsultantė(-as)
                testo rezultatams aptarti.
              </p>
            </div>
          </>
        )}
        {allSectionsCompleted && selectedProduct !== "firstaid" && (
          <>
            <div className="dashboard__message-container">
              <p className="dashboard__message">
                {isEnglish ? "Test completed successfully!" : "Testas atliktas sėkmingai!"}
              </p>
            </div>
            <div className="dashboard__divider"></div>
            <p className="dashboard__message">
              {isEnglish ? (
                <>
                  You can download your certificate here. Click <b>"Preview certificate"</b> to view
                  and download your certificate.
                </>
              ) : (
                <>
                  Galite atsisiųsti savo sertifikatą jau čia. Pasirinkite{" "}
                  <b>"Peržiūrėti pažymėjimą"</b>, kad pamatytumėte ir atsisiųstumėte savo
                  pažymėjimą.
                </>
              )}
            </p>

            <button className="dashboard__certificate-button" onClick={() => setPdfModalOpen(true)}>
              {isEnglish ? "Preview certificate" : "Peržiūrėti pažymėjimą"}
            </button>
            <div className="dashboard__divider"></div>
            <CertificateModal
              isOpen={isPdfModalOpen}
              isEnglish={isEnglish}
              selectedProduct={selectedProduct}
              pdfBlob={pdfBlob}
              name={name}
              surname={surname}
              birthDate={birthDate}
              fieldCode={fieldCode}
              agreed={agreed}
              emailSent={emailSent}
              sendingEmail={sendingEmail}
              emailError={emailError}
              optionalEmail={optionalEmail}
              userEmail={user?.email}
              hasUserData={!!userData}
              onClose={() => setPdfModalOpen(false)}
              onNameChange={setName}
              onSurnameChange={setSurname}
              onBirthDateChange={setBirthDate}
              onFieldCodeChange={setFieldCode}
              onAgreedChange={setAgreed}
              onSendCertificate={handleSendCertificateEmail}
            />
          </>
        )}
        <DashboardIntro selectedProduct={selectedProduct} />
        <h2 className={"dashboard__heading"}>{isEnglish ? "Choose topic" : "Pasirinkite temą"}</h2>
        <div className={"dashboard__content"}>
          <ul className={"dashboard__list"}>
            {sections.map((section, index) => (
              <div className={"dashboard__list-item-container"} key={index}>
                <button
                  className={"dashboard__list-item"}
                  onClick={() => handleSectionClick(index + 1, section.type, section.nextRoute)}
                  disabled={index + 1 > enabledSections}
                >
                  {section.title}
                </button>
                {index + 1 < enabledSections && (
                  <img className="dashboard__done" src="/assets/checkmark.svg" alt="checkmark" />
                )}
              </div>
            ))}
          </ul>
        </div>
        {allSectionsCompleted && (
          <button onClick={handleRestartClick} className="dashboard__restart-button">
            {isEnglish ? "Start again" : "Spręsti dar kartą"}
          </button>
        )}
      </div>
    </Container>
  );
};

export default Dashboard;
